import { CheckOutlined, FileAddOutlined } from '@ant-design/icons';
import { message, Modal, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FundraisingCandidate, FundraisingLinkAPI, UnlinkedContract } from '../../Api/FundraisingLinkAPI';

export interface FundraisingLinkRow extends UnlinkedContract {
    fundraisingId?: string;
    dcName?: string;
    dcVehicle?: string;
    dcCommitment?: number;
}

export const useTableFundraisingLink = (baseURL: string | undefined, clientId: string, moduleRole: string) => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<FundraisingLinkRow[]>([]);
    const [candidates, setCandidates] = useState<FundraisingCandidate[]>([]);
    const api = useMemo(() => FundraisingLinkAPI(baseURL, clientId), [baseURL, clientId]);

    useEffect(() => {
        Promise.all([api.getUnlinkedContracts(), api.getFundraisingCandidates()])
            .then(([contracts, fundraisingCandidates]) => {
                setRows(contracts
                    .filter(contract => contract.idPersonType !== 2 || Boolean(contract.companyName?.trim()))
                    .map(contract => contract.idPersonType === 2
                        ? { ...contract, name: contract.companyName!.trim() }
                        : contract));
                setCandidates(fundraisingCandidates);
            })
            .finally(() => setLoading(false));
    }, [api]);

    const setFundraisingSelection = useCallback((newData: FundraisingLinkRow, fundraisingId: string) => {
        const candidate = candidates.find(item => item.id === fundraisingId);
        newData.fundraisingId = fundraisingId;
        newData.dcName = candidate?.name;
        newData.dcVehicle = candidate?.vehicle;
        newData.dcCommitment = candidate?.commitment;
    }, [candidates]);

    const refreshFundraisingCandidates = useCallback(async () => {
        setCandidates(await api.getFundraisingCandidates());
    }, [api]);

    const createFundraisingProcess = useCallback((row: FundraisingLinkRow) => {
        const companyName = row.idPersonType === 2 ? row.companyName?.trim() : row.name.trim();
        const idVehicle = row.idVehicle;

        if (!companyName || !idVehicle) {
            message.error('El contrato no tiene los datos necesarios para crear el Fundraise process.');
            return;
        }

        Modal.confirm({
            title: 'Crear Fundraise process',
            content: `Se creará un Fundraise process para ${companyName}. ¿Quieres continuar?`,
            okText: 'Crear',
            cancelText: 'Cancelar',
            onOk: async () => {
                setLoading(true);
                try {
                    await api.createFundraisingProcess({
                        companyName,
                        idVehicle,
                        estimatedCloseDate: new Date().toISOString()
                    });
                    await refreshFundraisingCandidates();
                    message.success('Fundraise process creado correctamente.');
                } catch {
                    message.error('No se ha podido crear el Fundraise process.');
                    return Promise.reject();
                } finally {
                    setLoading(false);
                }

                return undefined;
            }
        });
    }, [api, refreshFundraisingCandidates]);

    const confirmLink = useCallback(async (row: FundraisingLinkRow) => {
        if (!row.fundraisingId) {
            message.warning('Selecciona un Fundraising ID antes de vincular el contrato.');
            return;
        }

        setLoading(true);
        try {
            await api.saveFundraisingProccessId(row.contractId, row.fundraisingId);
            setRows(currentRows => currentRows.filter(item => item.contractId !== row.contractId));
            message.success(`Fundraise process ${row.fundraisingId} vinculado al contrato ${row.contractId}.`);
        } catch {
            message.error(`No se ha podido vincular el contrato ${row.contractId}.`);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const renderLinkAction = useCallback((cell: any) => (
        <Tooltip title={cell.data.fundraisingId ? 'Vincular contrato' : 'Selecciona un Fundraising ID'}>
            <button
                className="fundraising-link-action"
                type="button"
                disabled={moduleRole === 'Read' || !cell.data.fundraisingId}
                onClick={() => confirmLink(cell.data)}
                aria-label={`Vincular contrato ${cell.data.contractId}`}
            >
                <CheckOutlined />
            </button>
        </Tooltip>
    ), [confirmLink, moduleRole]);

    const renderCreateAction = useCallback((cell: any) => (
        <Tooltip title="Crear Fundraise process">
            <button
                className="fundraising-link-action"
                type="button"
                disabled={moduleRole === 'Read' || !cell.data.idVehicle}
                onClick={() => createFundraisingProcess(cell.data)}
                aria-label={`Crear Fundraise process para el contrato ${cell.data.contractId}`}
            >
                <FileAddOutlined />
            </button>
        </Tooltip>
    ), [createFundraisingProcess, moduleRole]);

    return { loading, rows, candidates, setFundraisingSelection, renderLinkAction, renderCreateAction };
};
